import React, { useState, useRef } from "react";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate } from "react-router-dom";
import { Tooltip, Typography } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';

const MathKeyboardPage = () => {
    const navigate = useNavigate()
    const [latexInput, setLatexInput] = useState("");
    const inputRef = useRef(null);

    const insertLatex = (latex, cursorOffset = 0) => {
        const input = inputRef.current;
        if (!input) return;

        const startPos = input.selectionStart;
        const endPos = input.selectionEnd;
        const newValue = latexInput.substring(0, startPos) + " " + latex + latexInput.substring(endPos);

        setLatexInput(newValue);

        const newCursorPos = startPos + latex.indexOf("{}") + cursorOffset + 2;
        setTimeout(() => input.setSelectionRange(newCursorPos, newCursorPos), 10);
        input.focus();
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(latexInput).then(() => alert("Copied to clipboard!"));
    };

    const renderMixedContent = () => {
        let parts = latexInput.split(/(\$[^$]+\$)/g);
        return parts.map((part, index) => {
            if (part.startsWith("$") && part.endsWith("$")) {
                console.log(part, "part");
                let modifiedPart = part.replace(/\$/g, "");
                console.log(modifiedPart, "modifiedPart");
                return <InlineMath key={index} math={modifiedPart} />;
            }
            return <span key={index}>{part}</span>;
        });
    };

    const infoText = `Wrap math expressions with $...$ symbols. Use $...$ for inline math expressions (e.g., $\\sqrt{a + b}$). Use { } to insert variables. Click on the symbols below to insert them easily.`;
    const infoText2 = `Do not use $...$ symbols for Bold, Italic, or Underline tags.`;

    return (
        <div style={styles.container}>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '60px', justifyContent: 'space-between' }}>
                <button
                    onClick={() => navigate('/admin/quiz')}
                    style={{
                        marginRight: '5px',
                        color: 'white',
                        border: '0',
                        padding: '0 ',
                        outline: 'none',
                        cursor:'pointer',
                        background: 'transparent',
                        marginBottom: '14px',
                    }}
                >
                <KeyboardBackspaceIcon style={{fontSize:'35px',marginRight:'12px'}}/>
                </button>

                <h2 style={styles.title}>Math Expression Keyboard
                <Tooltip title={
                      <>
      <Typography variant="caption" style={{ marginBottom: '8px', display: 'block' }}>
        {infoText}
      </Typography>
      <Typography variant="caption">
       {infoText2}
      </Typography>
    </>
                }>
                    <InfoIcon style={{ marginLeft: '5px', marginBottom: '4px' }} />
                </Tooltip>
                </h2>

                <span/>
            </div>

            <input
                ref={inputRef}
                type="text"
                value={latexInput}
                onChange={(e) => setLatexInput(e.target.value)}
                placeholder="Enter LaTeX here..."
                style={styles.inputBox}
            />

            <div style={styles.previewBox}>{latexInput ? renderMixedContent() : <div style={{ fontSize: '18px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }} >Preview Here</div>}</div>

            <div style={styles.keyboardContainer}>
                {[
                    // HTML Formatting Tags
                    { label: "Bold", latex: "<b>YOUR TEXT</b>", offset: 4 },
                    { label: "Italic", latex: "<i>YOUR TEXT</i>", offset: 4 },
                    { label: "Underline", latex: "<u>YOUR TEXT</u>", offset: 4 },

                    // Arithmetic Operators
                    { label: "+", latex: "+" },
                    { label: "-", latex: "-" },
                    { label: "×", latex: "\\times" },
                    { label: "÷", latex: "\\div" },
                    { label: "=", latex: "=" },
                    { label: "≠", latex: "\\neq" },
                    { label: "≤", latex: "\\leq" },
                    { label: "≥", latex: "\\geq" },

                    // Exponents & Fractions
                    { label: "x²", latex: "x^{2}", offset: 3 },
                    { label: "Denominator", latex: "_{}", offset: 1 },
                    { label: "Exponent", latex: "^{}", offset: 1 },
                    { label: "Fraction", latex: "\\frac{}{}", offset: 1 },
                    { label: "√ Square Root", latex: "\\sqrt{}", offset: 1 },

                    // Trigonometry
                    { label: "sin", latex: "\\sin{}", offset: 1 },
                    { label: "cos", latex: "\\cos{}", offset: 1 },
                    { label: "tan", latex: "\\tan{}", offset: 1 },

                    // Greek Letters
                    { label: "π", latex: "\\pi" },
                    { label: "θ", latex: "\\theta" },
                    { label: "α", latex: "\\alpha" },
                    { label: "β", latex: "\\beta" },
                    { label: "γ", latex: "\\gamma" },

                    // Calculus & Sets
                    { label: "∫ Integral", latex: "\\int_{}^{}", offset: 5 },
                    { label: "∑ Sum", latex: "\\sum_{}^{}", offset: 5 },
                    { label: "∞ Infinity", latex: "\\infty" },
                    { label: "log", latex: "\\log_{}", offset: 1 },

                    // Degree, Modulus & Division
                    { label: "° Degree", latex: "^{\\circ}", offset: 3 },
                    { label: "Modulus", latex: "|x|", offset: 1 },
                    { label: "Division (a/b)", latex: "\\frac{a}{b}", offset: 4 },

                    // Additional symbols
                    { label: "∈", latex: "\\in" },
                    { label: "∉", latex: "\\notin" },
                    { label: "∪ Union", latex: "\\cup" },
                    { label: "∩ Intersection", latex: "\\cap" },
                ].map((btn, index) => (
                    <button key={index} style={styles.button} onClick={() => insertLatex(btn.latex, btn.offset || 0)}>
                        {btn.label}
                    </button>
                ))}
            </div>

            <div style={styles.actionButtons}>
                <button style={{ ...styles.actionButton, backgroundColor: "#28a745" }} onClick={copyToClipboard}>
                    Copy
                </button>
                <button style={{ ...styles.actionButton, backgroundColor: "#dc3545" }} onClick={() => setLatexInput("")}>
                    Clear
                </button>
            </div>
        </div>
    );
};

const styles = {
    container: {
        textAlign: "center",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "600px",
        margin: "auto",
    },
    title: {
        fontSize: "22px",
        fontWeight: "bold",
        marginBottom: "15px",
    },
    inputBox: {
        width: "100%",
        padding: "12px",
        fontSize: "16px",
        border: "2px solid #007bff",
        borderRadius: "8px",
        textAlign: "center",
        outline: "none",
    },
    previewBox: {
        marginTop: "15px",
        minHeight: "60px",
        background: "#f8f9fa",
        padding: "12px",
        borderRadius: "8px",
        fontSize: "18px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
    },
    keyboardContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        justifyContent: "center",
        marginTop: "20px",
    },
    button: {
        padding: "10px 12px",
        fontSize: "14px",
        backgroundColor: "#007bff",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
    actionButtons: {
        marginTop: "20px",
        display: "flex",
        justifyContent: "center",
        gap: "15px",
    },
    actionButton: {
        padding: "12px 18px",
        fontSize: "16px",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
};

export default MathKeyboardPage;
